import Core from "@/core/Core";
import BusEvent from "./BusEvent";

/**
 * Централизованная шина событий для приложения
 * Предоставляет типизированный и контролируемый интерфейс для работы с событиями
 * События регистрируются вручную для обеспечения IDE поддержки
 *
 * Доступ только через Core:
 *   Core.eventBus.FeedReload.emit(payload);
 *   Core.eventBus.FeedReload.on(handler);
 *   Core.eventBus.FeedReload.off(handler);
 *
 * В компонентах:
 *   this.$core.eventBus.FeedReload.emit(payload);
 *   this.$core.eventBus.FeedReload.on(handler);
 *   this.$core.eventBus.FeedReload.off(handler);
 *
 * ВАЖНО: Не использовать this.$eventBus — доступ только через this.$core.eventBus
 */

class EventBus {

  // Добавляйте новые события здесь
  // Это обеспечит автокомплит и статический анализ в IDE

  // === СИСТЕМНЫЕ СОБЫТИЯ ===
  static AppReady            = new BusEvent('app:ready');
  static AppError            = new BusEvent('app:error');

  // === СОБЫТИЯ ACL (АВТОРИЗАЦИЯ) ===
  static AclLogin            = new BusEvent('acl:login');
  static AclLogout           = new BusEvent('acl:logout');
  static AclSessionExpired   = new BusEvent('acl:session:expired');
  static AclSessionRestored  = new BusEvent('acl:session:restored');
  static AclReady            = new BusEvent('acl:ready');

  // === UI СОБЫТИЯ ===
  static UiModalOpened       = new BusEvent('ui:modal:opened');
  static UiModalClosed       = new BusEvent('ui:modal:closed');

  // === СОБЫТИЯ ФАЙЛОВ ===
  static FilesUploaded            = new BusEvent('files:uploaded');               // Файл(ы) загружены
  static FilesDeleted             = new BusEvent('files:deleted');                // Файл(ы) удалены
  static FilesDownloaded          = new BusEvent('files:downloaded');             // Файл(ы) скачаны
  static FilesUploadProgress      = new BusEvent('files:upload:progress');        // Прогресс загрузки
  static FilesDragStart           = new BusEvent('files:drag:start');             // Начало перетаскивания
  static FilesDragStop            = new BusEvent('files:drag:stop');              // Конец перетаскивания
  static FilesDroppedOutsideZone  = new BusEvent('files:dropped');                // Файлы брошены вне дроп зоны

  // === СПРАВОЧНИКИ ===
  static DictionariesDownloaded = new BusEvent('dictionaries:downloaded');        // Все справочники загрузились

  // === СОБЫТИЯ ФИДА ===
  /** чтобы стригерить обновление фида в соседнем компоненте, используется в партнёре, позиции заказа и тому подобных, где показан фид */
  static FeedReload = new BusEvent('feed:reload');

  // === ОБНОВЛЕНИЕ ВНЕШНЕГО ФИЛЬТРА ===
  /** он находится справа сверху на страницах с реестрами, реестры слушают это событие и запускают renew */
  static OuterfilterUpdated = new BusEvent('outerfilter:update');

  // === СОБЫТИЯ ЗАКАЗОВ ===
  static OrdersUpdated = new BusEvent('orders:update');

  // === СОБЫТИЯ ПАРТНЁРА ===
  /** событие используется при обновлении партнёра в карточке партнёра, по сути
   * костыль, можно сделать элегантнее */
  static PartnerUpdated = new BusEvent('partner:update');

  /** Тоже костыль связанный с карточкой партнёра, по идее её всю нужно отрефакторить,
   * там код старлексея уже 4 летней давности, этот эмит всего используется в одном месте */
  static BankaccountUpdated = new BusEvent('bankaccount:update');

  // === СОБЫТИЯ СКЛАДА ===
  static StoreUpdated = new BusEvent('store:updated');

  /**
   * Map для хранения слушателей событий
   */
  static #listeners: Map<string, Function[]> = new Map();

  static #initialized: boolean = false;

  /**
   * Инициализация EventBus
   */
  static async init(): Promise<void> {
    if (this.#initialized) {
      Core.error('EventBus уже инициализирован');
      return;
    }

    this.#initialized = true;
  }

  /**
   * Подписка на событие
   */
  static addEventListener(event: string, callback: Function): void {
    if (!this.#listeners.has(event)) {
      this.#listeners.set(event, []);
    }

    this.#listeners.get(event)!
        .push(callback);
  }

  /**
   * Алиас для addEventListener
   */
  static on(event: string, callback: Function): void {
    return this.addEventListener(event, callback);
  }

  /**
   * Отписка от события
   */
  static removeEventListener(event: string, callback?: Function): void {
    const callbacks = this.#listeners.get(event);
    if (!callbacks) {
      return;
    }

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
    else {
      this.#listeners.delete(event);
    }
  }

  /**
   * Алиас для removeEventListener
   */
  static off(event: string, callback?: Function): void {
    return this.removeEventListener(event, callback);
  }

  /**
   * Генерация события
   */
  static emit(event: string, ...args: any[]): void {
    const callbacks = this.#listeners.get(event);

    if (callbacks) {
      // создаем поверхностную копию массива, поэтому даже если оригинальный массив изменится
      // во время выполнения, итерация пройдет по всем callback'ам, которые были на момент вызова
      callbacks.slice().forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          Core.error(`Ошибка в обработчике события ${event} - ${callback}:`, error);
          // TODO репорт ошибки в сентри по идее, или воткнуть его в Core.error?
        }
      });
    }
  }

  /**
   * Одноразовая подписка на событие
   */
  static once(event: string, callback: Function): void {
    const onceCallback = (...args: any[]) => {
      callback(...args);
      this.removeEventListener(event, onceCallback);
    };

    this.addEventListener(event, onceCallback);
  }

  /**
   * Очистить все слушатели
   */
  static removeAllListeners(): void {
    this.#listeners.clear();
  }

  static get isInitialized(): boolean {
    return this.#initialized;
  }
}

export default EventBus;
